import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Zap, Sparkles, ArrowLeft } from "lucide-react";
import api, { errMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TIERS = [
  {
    id: "starter",
    name: "Starter",
    monthly: 132.97,
    yearly: 1329.70,
    description: "For solo wholesalers testing markets.",
    features: [
      "3 saved markets",
      "Alerts on 1 market",
      "10 AI insights / month",
      "Email alerts",
      "All MAO/ARV calculators",
    ],
    cta: "Start 14-day trial",
    accent: false,
  },
  {
    id: "growth",
    name: "Growth",
    monthly: 332.47,
    yearly: 3324.70,
    description: "For active wholesalers running multiple markets.",
    features: [
      "15 saved markets",
      "Alerts on 5 markets",
      "50 AI insights / month",
      "Email + SMS alerts",
      "Buyer's list + auto-blast",
    ],
    cta: "Start 14-day trial",
    accent: true,
  },
  {
    id: "pro",
    name: "Pro",
    monthly: 531.97,
    yearly: 5319.70,
    description: "Hunt unlimited deals across every market you want.",
    features: [
      "Unlimited saved markets",
      "Unlimited alerts",
      "Unlimited AI insights",
      "SMS + email alerts",
      "Priority Stripe support",
    ],
    cta: "Start 14-day trial",
    accent: false,
  },
];

const fmt = (n) =>
  Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Pricing() {
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState(null);
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    if (!loading && user) {
      api.get("/billing/me").then((r) => setBilling(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- api is module-level singleton
  }, [user, loading]);

  const startCheckout = async (tierId) => {
    if (!user) {
      nav("/register");
      return;
    }
    setBusy(tierId);
    const planKey = `${tierId}_${cycle === "yearly" ? "yearly" : "monthly"}`;
    try {
      const r = await api.post("/billing/checkout", {
        plan_key: planKey,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(errMessage(e, "Could not start checkout"));
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-[--bg]">
      <header className="border-b border-[--line] bg-white px-8 py-4 flex items-center justify-between">
        <button
          data-testid="pricing-back"
          onClick={() => nav(user ? "/dashboard" : "/login")}
          className="text-sm text-[--ink-muted] hover:text-[--ink] flex items-center gap-1.5"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Back
        </button>
        <div className="overline">// Pricing</div>
      </header>

      <div className="px-6 py-16 max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <div className="overline mb-3">14-day free trial · cancel anytime</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tighter mb-4">
            Find the deals.<br/>Keep the deals.
          </h1>
          <p className="text-[--ink-soft] max-w-xl mx-auto">
            Trial any plan free for 14 days. We won't charge until day 15 — cancel anytime from the billing portal.
          </p>
        </div>

        <div className="flex justify-center mb-10">
          <div className="inline-flex border border-[--line] bg-white">
            {["monthly", "yearly"].map((c) => (
              <button
                key={c}
                data-testid={`cycle-${c}`}
                onClick={() => setCycle(c)}
                className={`px-6 py-2 text-sm transition-colors ${
                  cycle === c ? "bg-[--ink] text-white" : "text-[--ink-soft] hover:text-[--ink]"
                }`}
              >
                {c === "monthly" ? "Monthly" : "Yearly"}
                {c === "yearly" && <span className="ml-2 text-[10px] font-mono uppercase tracking-wider opacity-70">save 17%</span>}
              </button>
            ))}
          </div>
        </div>

        {billing?.has_active_access && (
          <div className="surface-card p-4 mb-8 flex items-center justify-between">
            <div className="text-sm">
              <span className="overline mr-2">Current</span>
              {billing.is_grandfathered ? "Comp Pro (grandfathered)" : `${billing.subscription_tier} · ${billing.subscription_status}`}
            </div>
            <button
              data-testid="open-portal-btn"
              onClick={async () => {
                try {
                  const r = await api.post("/billing/portal", { return_url: window.location.origin + "/billing" });
                  window.location.href = r.data.url;
                } catch (e) {
                  toast.error("Portal not available — no Stripe customer yet");
                }
              }}
              className="btn-ghost text-xs"
            >
              Manage subscription
            </button>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-4">
          {TIERS.map((t) => {
            const price = cycle === "monthly" ? t.monthly : t.yearly / 12;
            return (
              <div
                key={t.id}
                data-testid={`tier-card-${t.id}`}
                className={`surface-card p-8 ${
                  t.accent ? "border-2 border-[--ink]" : ""
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="font-display text-2xl font-bold">{t.name}</div>
                  {t.accent && (
                    <span className="text-[10px] px-2 py-0.5 bg-[--ink] text-white uppercase tracking-wider">
                      Recommended
                    </span>
                  )}
                </div>
                <p className="text-sm text-[--ink-soft] mb-5">{t.description}</p>
                <div className="mb-5">
                  <div className="font-display text-5xl font-black tracking-tighter">
                    ${fmt(price)}
                    <span className="text-sm font-mono text-[--ink-muted] font-normal ml-1">/mo</span>
                  </div>
                  {cycle === "yearly" && (
                    <div className="text-xs text-[--ink-muted] font-mono mt-1">
                      ${fmt(t.yearly)}/yr billed annually
                    </div>
                  )}
                </div>
                <ul className="space-y-2 mb-6 text-sm">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={14} strokeWidth={2} className="text-[--accent] mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`subscribe-${t.id}`}
                  onClick={() => startCheckout(t.id)}
                  disabled={busy === t.id}
                  className={`w-full ${t.accent ? "btn-primary" : "btn-ghost"} flex items-center justify-center gap-2 py-3`}
                >
                  {busy === t.id ? "Redirecting…" : (
                    <>
                      {t.accent ? <Sparkles size={14} strokeWidth={1.5} /> : <Zap size={14} strokeWidth={1.5} />}
                      {t.cta}
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-[--ink-muted] text-center mt-10 font-mono">
          Powered by Stripe · cards secured · no charge during trial
        </p>
      </div>
    </div>
  );
}
